import { Component, Element, Event, EventEmitter, h, Prop } from '@stencil/core'
import { AskFathomLoading } from 'src/ask-fathom/ui'
import { FathomLogo } from 'src/customize/integrations/logos'
import { dayjs } from 'src/lib/dayjs'
import { PlaybackStore } from 'src/page/call/detail/state'

import { IAskFathomMessage } from '../types'

@Component({
  tag: 'ask-fathom-message',
})
export class AskFathomMessage {
  @Event({ bubbles: false }) messageLoaded: EventEmitter<HTMLAskFathomMessageElement>

  @Element() el: HTMLAskFathomMessageElement

  @Prop() message: IAskFathomMessage

  componentDidLoad() {
    // Broadcast new message element so we can scroll to it in the ui-scrollarea
    this.messageLoaded.emit(this.el)
  }

  render() {
    const loading = this.message.isLoadingContent || this.message.isLoadingClips

    // User Message
    if (this.message.role === 'user') {
      return (
        <figure class='flex relative flex-col items-end mt-6' data-message-id={this.message.id}>
          <blockquote class='flex relative flex-col justify-center self-stretch'>
            <p class='relative self-end p-2 w-3/5 text-md font-medium whitespace-pre-wrap bg-gray-850 rounded-b-md rounded-tl-md border-surface-higher'>
              {this.message.content}
            </p>
          </blockquote>
        </figure>
      )
    }

    // Assistant Message (Fathom AI)
    return (
      <figure class='flex relative flex-col items-end mt-6' data-message-id={this.message.id}>
        <div class='flex gap-2 self-stretch'>
          <div class='flex overflow-hidden justify-center items-center w-5 h-5 bg-gray-800 rounded-full'>
            <FathomLogo class='w-3' />
          </div>
          <blockquote class='flex flex-col w-4/5 text-md'>
            {this.message.content && (
              <div class='mb-2'>
                <preview-markdown small source={this.message.content} />
              </div>
            )}
            {this.message.clips && (
              <ul>
                {this.message.clips?.map((clip) => {
                  const timestamp = dayjs
                    .duration(clip.play_from_point, 's')
                    .format(clip.play_from_point >= 3600 ? '@ H:mm:ss' : '@ m:ss')

                  if (clip.call?.permalink) {
                    const isPastYear = dayjs(clip.call.started_at).isBefore(dayjs(), 'year')
                    const callDate = dayjs(clip.call.started_at).format(isPastYear ? 'LL' : 'MMMM D')

                    return (
                      <a href={clip.call.permalink} key={clip.play_from_point} target='_blank'>
                        <li class='p-8px mb-2px text-brand bg-gray-850'>
                          <div class='flex gap-2'>
                            <ui-icon icon='play-circle-fill' class='text-xlarge' />
                            <div>
                              <div class='mb-1 font-bold'>{clip.title}</div>
                              <div class='text-small'>
                                {callDate} {timestamp}
                              </div>
                            </div>
                          </div>
                        </li>
                      </a>
                    )
                  }

                  return (
                    <li
                      key={clip.play_from_point}
                      class='flex gap-2 p-8px mb-2px text-brand bg-gray-850 hover:bg-opacity-80 cursor-pointer'
                      onClick={() => (PlaybackStore.state.seekTime = clip.play_from_point)}
                    >
                      <ui-icon icon='play-circle-fill' class='text-xlarge' />
                      <div class='cursor-pointer' onClick={() => (PlaybackStore.state.seekTime = clip.play_from_point)}>
                        {clip.title} {timestamp}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
            {loading && <AskFathomLoading className='mb-2' />}
            {this.message.hasLoadingContentError && (
              <p class='mb-2 w-4/5 text-sm italic'>
                Sorry, I'm too overloaded at the moment to process your question. Can you try again in a few minutes?
              </p>
            )}
          </blockquote>
        </div>
      </figure>
    )
  }
}
